import { useRouter } from "expo-router";
import { ExternalLink, X } from "lucide-react-native";
import { FC, useMemo, useState } from "react";

import { Box, Card, HStack, Pressable, Text, VStack } from "@/components/ui";

import {
  formatMargin,
  formatPercent,
  PARTY_COLOR,
  STRENGTH_LABEL,
} from "./colors";
import InfoTip from "./InfoTip";
import {
  contextStats,
  type County2024,
  crosstabs,
  GOVERNOR_PULL,
  INDEPENDENT_KEEP,
  NON_VOTER_SHARE,
  POLL_MAX_WEIGHT,
  POLL_WINDOW_DAYS,
  trumpTwoParty,
} from "./model";
import { POLLS_AS_OF } from "./polls";
import { FINANCES, ISSUES, shortName } from "./survey";
import type {
  Component,
  EarlyVoteCounts,
  ModelSettings,
  Office,
  Party,
  StateInfo,
  TrackerVariant,
} from "./types";
import useEarlyVote from "./useEarlyVote";
import { describeSide, OFFICE_LABEL, RaceRow } from "./view";

/* ── Types ─────────────────────────────────────── */

interface RacePanelProps {
  county2024: County2024 | null;
  /** The `turnoutEdge` the row was built with. */
  edge: number;
  /** The `nationalEnvironment` the row was built with. */
  environment: number | null;
  office: Office;
  onClose: () => void;
  row: RaceRow;
  settings: ModelSettings;
  sourceLabel: string | null;
  /** The survey's study, linked in the admin view. */
  studyId: string | null;
  variant: TrackerVariant;
}

/* ── Constants ─────────────────────────────────── */

/** Counties listed before "Show all" in the early-vote section. */
const EARLY_VOTE_COUNTIES = 5;

const EARLY_VOTE_HINT =
  "Ballots already cast, as counted by election officials. Party is the voter's registration, not their vote, and each party's habit of voting early shifts between elections, so these figures are context only: they don't move the forecast.";

/* ── Helpers ───────────────────────────────────── */

const formatDay = (date: string): string =>
  new Date(`${date}T12:00:00`).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
  });

/** "D 40% · R 25% · other 35%" by registration, null when not reported. */
const partySplit = (counts: EarlyVoteCounts): string | null => {
  if (counts.dem === null || counts.rep === null) return null;
  const known = counts.dem + counts.rep + (counts.other ?? 0);
  if (known <= 0) return null;
  return [
    `D ${formatPercent(counts.dem / known)}`,
    `R ${formatPercent(counts.rep / known)}`,
    ...(counts.other === null
      ? []
      : [`other ${formatPercent(counts.other / known)}`]),
  ].join(" · ");
};

/* ── Subcomponents ─────────────────────────────── */

/** Two-party split bar: left share vs the Republican share. */
const ShareBar: FC<{ leftParty: Party; share: number | null }> = ({
  leftParty,
  share,
}) =>
  share === null ? (
    <Box className="h-2 flex-1 rounded-full bg-background-100" />
  ) : (
    <HStack className="h-2 flex-1 gap-0.5 rounded-full overflow-hidden">
      <Box
        style={{
          backgroundColor: PARTY_COLOR[leftParty],
          flexBasis: 0,
          flexGrow: share,
        }}
      />
      <Box
        style={{
          backgroundColor: PARTY_COLOR.R,
          flexBasis: 0,
          flexGrow: 1 - share,
        }}
      />
    </HStack>
  );

const ReadRow: FC<{
  component: Component | null;
  hint: string;
  label: string;
  leftParty: Party;
}> = ({ component, hint, label, leftParty }) => (
  <HStack className="items-center gap-3">
    <HStack className="w-44 items-center gap-1">
      <Text className="text-sm text-typography-800" numberOfLines={1}>
        {label}
      </Text>
      <InfoTip text={hint} />
    </HStack>
    <ShareBar leftParty={leftParty} share={component?.leftShare ?? null} />
    <Text className="w-28 text-right text-sm font-semibold text-typography-900">
      {component
        ? formatMargin((2 * component.leftShare - 1) * 100, leftParty)
        : "—"}
    </Text>
  </HStack>
);

/** A shift of the read toward one side, in margin points. */
const AdjustmentRow: FC<{
  hint: string;
  label: string;
  leftParty: Party;
  note: string;
  points: number;
}> = ({ hint, label, leftParty, note, points }) => (
  <HStack className="items-center gap-3">
    <HStack className="w-44 items-center gap-1">
      <Text className="text-sm text-typography-800" numberOfLines={1}>
        {label}
      </Text>
      <InfoTip text={hint} />
    </HStack>
    <Text className="flex-1 text-xs text-typography-500">{note}</Text>
    <Text className="w-28 text-right text-sm font-semibold text-typography-900">
      {formatMargin(points, leftParty)}
    </Text>
  </HStack>
);

const SectionTitle: FC<{ title: string }> = ({ title }) => (
  <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
    {title}
  </Text>
);

const EarlyVoteSection: FC<{ state: StateInfo }> = ({ state }) => {
  const { earlyVote, status } = useEarlyVote(state.abbr);
  const [expanded, setExpanded] = useState(false);
  if (status === "loading") return null;

  const statewide = earlyVote?.state;
  const split = statewide ? partySplit(statewide) : null;
  const counties = earlyVote
    ? expanded
      ? earlyVote.counties
      : earlyVote.counties.slice(0, EARLY_VOTE_COUNTIES)
    : [];

  return (
    <VStack className="gap-1.5">
      <HStack className="items-center gap-1">
        <SectionTitle title="Early vote so far" />
        <InfoTip text={EARLY_VOTE_HINT} />
      </HStack>
      {status === "error" ? (
        <Text className="text-sm text-typography-500">
          {"Couldn't load the early-vote figures."}
        </Text>
      ) : !earlyVote || !statewide ? (
        <Text className="text-sm text-typography-500">
          No early-vote figures reported for {state.name}.
        </Text>
      ) : (
        <>
          <Text className="text-sm text-typography-800">
            {statewide.total.toLocaleString("en-US")} ballots by{" "}
            {formatDay(earlyVote.date)}
            {earlyVote.daysOut === null
              ? ""
              : ` (${earlyVote.daysOut} ${earlyVote.daysOut === 1 ? "day" : "days"} out)`}{" "}
            · {formatPercent(statewide.total / state.pres2024.total)} of the
            2024 vote
            {statewide.mail === null || statewide.inPerson === null
              ? ""
              : ` · ${formatPercent(statewide.mail / statewide.total)} by mail`}
          </Text>
          {split && (
            <Text className="text-sm text-typography-800">
              By registration: {split}
            </Text>
          )}
          {counties.map((county) => {
            const countySplit = partySplit(county);
            return (
              <HStack className="items-center gap-3" key={county.name}>
                <Text
                  className="flex-1 text-sm text-typography-700"
                  numberOfLines={1}
                >
                  {county.name}
                </Text>
                <Text className="text-xs text-typography-600">
                  {county.total.toLocaleString("en-US")}
                  {countySplit ? ` · ${countySplit}` : ""}
                </Text>
              </HStack>
            );
          })}
          {earlyVote.counties.length > EARLY_VOTE_COUNTIES && (
            <Pressable
              className="self-start px-1"
              onPress={() => setExpanded((open) => !open)}
            >
              <Text className="text-xs font-medium text-primary-600">
                {expanded
                  ? "Show the largest only"
                  : `Show all ${earlyVote.counties.length} counties`}
              </Text>
            </Pressable>
          )}
          <Text className="text-xs text-typography-400">
            {earlyVote.credit}
          </Text>
        </>
      )}
    </VStack>
  );
};

/* ── Component ─────────────────────────────────── */

const RacePanel: FC<RacePanelProps> = ({
  county2024,
  edge,
  environment,
  office,
  onClose,
  row,
  settings,
  sourceLabel,
  studyId,
  variant,
}) => {
  const router = useRouter();
  const { estimate, leftParty, race, sample, state } = row;
  const context = useMemo(
    () => ({ county2024, edge, environment, settings, state }),
    [county2024, edge, environment, settings, state]
  );
  const tabs = useMemo(
    () => (sample && estimate ? crosstabs(sample, office, context) : []),
    [context, estimate, office, sample]
  );
  const stats = useMemo(
    () => (sample ? contextStats(sample, context) : null),
    [context, sample]
  );
  const issueShares = stats?.issues ?? null;
  const topIssues = issueShares
    ? ISSUES.map((label, i) => ({ label, share: issueShares[i] }))
        .sort((a, b) => b.share - a.share)
        .slice(0, 3)
    : [];
  const financeShares = stats?.finances ?? null;
  const leftName = race?.left ? shortName(race.left) : "Democrats";
  const gap =
    estimate && row.externalProbability !== null
      ? (estimate.leftProbability - row.externalProbability) * 100
      : null;

  return (
    <Card className="p-5 rounded-2xl border border-outline-200">
      <VStack className="gap-5">
        {/* Header */}
        <HStack className="items-start justify-between gap-3">
          <VStack className="flex-1 gap-0.5">
            <Text className="text-lg font-semibold text-typography-900">
              {state.name} · {OFFICE_LABEL[office]}
              {race?.special ? " (special)" : ""}
            </Text>
            {race ? (
              <Text className="text-sm text-typography-600">
                {describeSide(race, "left")} vs {describeSide(race, "right")}
                {race.others.length > 0
                  ? ` · also ${race.others.map((c) => `${c.name} (${c.party})`).join(", ")}`
                  : ""}
              </Text>
            ) : (
              <Text className="text-sm text-typography-600">
                Generic ballot: Democratic vs Republican House candidate
              </Text>
            )}
            {race && (
              <Text className="text-xs text-typography-400">
                Held by {race.incumbentParty} ({race.incumbent}
                {race.incumbentRunning ? ", running" : ", not running"})
                {race.cook && variant === "admin"
                  ? ` · Cook: ${race.cook}`
                  : ""}
                {race.notes ? ` · ${race.notes}` : ""}
              </Text>
            )}
          </VStack>
          <Pressable
            className="p-1 rounded-md hover:bg-background-100"
            onPress={onClose}
          >
            <X color="rgb(var(--color-typography-500))" size={18} />
          </Pressable>
        </HStack>

        {/* Headline reads */}
        <HStack className="gap-3">
          <VStack className="flex-1 gap-0.5 rounded-xl bg-background-50 px-4 py-3">
            <Text className="text-xs text-typography-500">Foresight</Text>
            <Text className="text-xl font-bold text-typography-900">
              {estimate ? formatMargin(estimate.margin, leftParty) : "—"}
            </Text>
            <Text className="text-xs text-typography-500">
              {estimate
                ? `${formatPercent(estimate.leftProbability)} ${leftParty} win · ${STRENGTH_LABEL[row.call.strength]}`
                : "Not surveyed yet"}
            </Text>
          </VStack>
          {sourceLabel && (
            <VStack className="flex-1 gap-0.5 rounded-xl bg-background-50 px-4 py-3">
              <Text className="text-xs text-typography-500" numberOfLines={1}>
                {sourceLabel}
              </Text>
              <Text className="text-xl font-bold text-typography-900">
                {row.externalProbability === null
                  ? "—"
                  : `${formatPercent(row.externalProbability)} ${leftParty}`}
              </Text>
              <Text className="text-xs text-typography-500">
                {row.external?.margin !== null &&
                row.external?.margin !== undefined
                  ? `Margin ${formatMargin(row.external.margin, leftParty)}`
                  : "Win probability"}
              </Text>
            </VStack>
          )}
          {gap !== null && (
            <VStack className="flex-1 gap-0.5 rounded-xl bg-background-50 px-4 py-3">
              <Text className="text-xs text-typography-500">Gap</Text>
              <Text className="text-xl font-bold text-typography-900">
                {gap > 0 ? "+" : "−"}
                {Math.abs(gap).toFixed(0)} pts
              </Text>
              <Text className="text-xs text-typography-500">
                Foresight {gap > 0 ? "more" : "less"} {leftParty}-leaning
              </Text>
            </VStack>
          )}
        </HStack>

        {estimate && sample ? (
          <>
            {/* Method breakdown */}
            <VStack className="gap-2">
              <SectionTitle title="How Foresight reads it" />
              <ReadRow
                component={estimate.own}
                hint={`'Who would you vote for?' among likely voters (weighted by stated turnout), reweighted so the sample's recalled 2024 vote matches the certified result, then for midterm turnout: 2024 non-voters capped at ${formatPercent(NON_VOTER_SHARE)} of likely voters, and the national turnout edge (see the turnout check below).`}
                label="Own vote, calibrated"
                leftParty={leftParty}
              />
              <ReadRow
                component={estimate.ownRaw}
                hint="Same question, weighted by stated turnout only: what the synthetic respondents say before the 2024 and turnout calibrations."
                label="Own vote, raw"
                leftParty={leftParty}
              />
              <ReadRow
                component={estimate.neighbors}
                hint="'How will the people around you vote?' — the social-circle question. Corrected by how far respondents misjudged their neighbors' 2024 vote."
                label="Neighbors"
                leftParty={leftParty}
              />
              {estimate.own && estimate.independentDiscount !== null && (
                <AdjustmentRow
                  hint={`The simulated support for this independent runs far beyond what ${state.name}'s partisanship suggests, and independents usually fade by Election Day, so the own vote keeps ${formatPercent(INDEPENDENT_KEEP)} of its gap to the state's 2024 presidential result (${formatMargin((1 - 2 * trumpTwoParty(state)) * 100)}): it counts as ${formatMargin((2 * estimate.own.leftShare - 1) * 100 + estimate.independentDiscount, leftParty)} in the blend.`}
                  label="Independent discount"
                  leftParty={leftParty}
                  note="on the own vote"
                  points={estimate.independentDiscount}
                />
              )}
              {race && estimate.incumbencyBonus !== null && (
                <AdjustmentRow
                  hint={`Governor races are more candidate-driven, and the simulated respondents barely reward incumbents, so ${race.incumbent}, running for re-election, gets ${Math.abs(estimate.incumbencyBonus)} points after the blend.`}
                  label="Incumbency bonus"
                  leftParty={leftParty}
                  note="on the margin"
                  points={estimate.incumbencyBonus}
                />
              )}
              {environment !== null && estimate.governorPull !== null && (
                <AdjustmentRow
                  hint={`The simulated respondents split tickets less than real voters, and governor races are where real voters split them most, so a governor margin keeps ${formatPercent(GOVERNOR_PULL)} of its distance from the national environment: the generic ballot read, ${formatMargin(environment)}.`}
                  label="National pull"
                  leftParty={leftParty}
                  note={`${formatPercent(1 - GOVERNOR_PULL)} toward ${formatMargin(environment)}`}
                  points={estimate.governorPull}
                />
              )}
              {variant === "admin" && estimate.polls && (
                <AdjustmentRow
                  hint={`Each pollster's latest public poll of this race in the ${POLL_WINDOW_DAYS} days before ${formatDay(POLLS_AS_OF)}, averaged on the two-party margin and blended in last: a third for one poll, half for two, at most ${formatPercent(POLL_MAX_WEIGHT)}.${estimate.polls.fade !== 0 ? ` Independents have run behind their polls, so the average first moves ${Math.abs(estimate.polls.fade)} points toward their opponent.` : ""} Clients see the blended read without this line.`}
                  label="Public polls"
                  leftParty={leftParty}
                  note={`${formatMargin(estimate.polls.margin, leftParty)} (${estimate.polls.n} poll${estimate.polls.n === 1 ? "" : "s"})${estimate.polls.fade !== 0 ? `, faded ${Math.abs(estimate.polls.fade)} pts` : ""} · ${formatPercent(estimate.polls.weight)} weight`}
                  points={estimate.polls.shift}
                />
              )}
              <Text className="text-xs text-typography-500">
                Blend: {Math.round(settings.ownWeight * 100)}% own vote /{" "}
                {Math.round((1 - settings.ownWeight) * 100)}% neighbors ·
                uncertainty ±{(1.96 * estimate.sigma).toFixed(0)} pts (95%) ·{" "}
                {formatPercent(estimate.undecided)} undecided
                {estimate.expectLeft !== null
                  ? ` · ${formatPercent(estimate.expectLeft)} expect ${leftName} to win`
                  : ""}
              </Text>
              {estimate.calibrationBias !== null && (
                <Text className="text-xs text-typography-500">
                  2024 check: the sample recalled Trump at{" "}
                  {(
                    trumpTwoParty(state) * 100 +
                    estimate.calibrationBias
                  ).toFixed(1)}
                  % of the two-party vote vs{" "}
                  {(trumpTwoParty(state) * 100).toFixed(1)}% actual (
                  {estimate.calibrationBias > 0 ? "+" : ""}
                  {estimate.calibrationBias.toFixed(1)} pts), corrected by
                  reweighting.
                </Text>
              )}
              <Text className="text-xs text-typography-500">
                {`Turnout check: 2024 non-voters are ${formatPercent(estimate.nonVoterShare)} of the sample's likely voters${estimate.nonVoterShare > NON_VOTER_SHARE ? `, capped at ${formatPercent(NON_VOTER_SHARE)}` : ""}; the national turnout edge weighs Harris voters ×${edge.toFixed(2)} and Trump voters ×${(1 / edge).toFixed(2)}.`}
              </Text>
            </VStack>

            {/* Crosstabs */}
            <VStack className="gap-1.5">
              <SectionTitle title="Who is voting how (own vote)" />
              {tabs.map((tab) => (
                <HStack
                  className="items-center gap-3"
                  key={`${tab.group}-${tab.label}`}
                >
                  <Text className="w-28 text-sm text-typography-700">
                    {tab.label}
                  </Text>
                  <ShareBar leftParty={leftParty} share={tab.leftShare} />
                  <Text className="w-28 text-right text-xs text-typography-600">
                    {tab.leftShare === null
                      ? "—"
                      : formatMargin(
                          (2 * tab.leftShare - 1) * 100,
                          leftParty
                        )}{" "}
                    · n {tab.n}
                  </Text>
                </HStack>
              ))}
            </VStack>

            {/* Context */}
            {stats && (
              <VStack className="gap-1.5">
                <SectionTitle title="Likely-voter mood" />
                <Text className="text-sm text-typography-800">
                  {stats.approve !== null && stats.disapprove !== null
                    ? `Trump approval ${formatPercent(stats.approve)} · disapproval ${formatPercent(stats.disapprove)} · already voted `
                    : "Already voted "}
                  {formatPercent(stats.alreadyVoted)}
                </Text>
                {topIssues.length > 0 && (
                  <Text className="text-sm text-typography-800">
                    Top issues:{" "}
                    {topIssues
                      .map(
                        (issue) =>
                          `${issue.label} ${formatPercent(issue.share)}`
                      )
                      .join(" · ")}
                  </Text>
                )}
                {financeShares && (
                  <Text className="text-sm text-typography-800">
                    Household vs a year ago:{" "}
                    {FINANCES.map(
                      (label, i) =>
                        `${label.toLowerCase()} ${formatPercent(financeShares[i])}`
                    ).join(" · ")}
                  </Text>
                )}
              </VStack>
            )}

            <EarlyVoteSection key={state.abbr} state={state} />

            {/* Verbatims */}
            {sample.verbatims.length > 0 && (
              <VStack className="gap-2">
                <SectionTitle title="In their words" />
                {sample.verbatims.slice(0, 4).map((v, i) => (
                  <VStack
                    className="gap-0.5 rounded-xl bg-background-50 px-4 py-3"
                    key={i}
                  >
                    <Text className="text-sm leading-5 text-typography-800">
                      “{v.quote}”
                    </Text>
                    <Text className="text-xs text-typography-500">
                      {[
                        v.gender === "female"
                          ? "Woman"
                          : v.gender === "male"
                            ? "Man"
                            : null,
                        v.age,
                        v.county,
                      ]
                        .filter((x) => x !== null)
                        .join(", ")}
                    </Text>
                  </VStack>
                ))}
              </VStack>
            )}

            {/* Sample */}
            <HStack className="items-center justify-between gap-3 border-t border-outline-200 pt-3">
              <Text className="flex-1 text-xs text-typography-400">
                Wave {sample.wave} · n ={" "}
                {sample.respondents.length.toLocaleString("en-US")} synthetic
                adults · run completed{" "}
                {new Date(sample.completedAt).toLocaleString("en-US", {
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  month: "short",
                })}
              </Text>
              {studyId && (
                <Pressable
                  className="flex-row items-center gap-1"
                  onPress={() => router.push(`/(dashboard)/studies/${studyId}`)}
                >
                  <Text className="text-xs font-medium text-primary-600">
                    Open study
                  </Text>
                  <ExternalLink
                    color="rgb(var(--color-primary-600))"
                    size={12}
                  />
                </Pressable>
              )}
            </HStack>
          </>
        ) : (
          <>
            <Text className="text-sm text-typography-500">
              {sample
                ? `The ${state.name} survey didn't include this office.${variant === "admin" ? " Field a new wave to add it." : ""}`
                : `No Foresight survey for ${state.name} yet.${variant === "admin" ? ' Use "Field wave" to survey it.' : ""}`}
            </Text>
            <EarlyVoteSection key={state.abbr} state={state} />
          </>
        )}
      </VStack>
    </Card>
  );
};

export default RacePanel;
